import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import NavBar from "@/components/NavBar";
import StatusPill from "@/components/StatusPill";
import api, { formatApiError } from "@/lib/api";
import { taskUrgency, sortByUrgency } from "@/lib/taskUrgency";
import { useAuth } from "@/contexts/AuthContext";
import { AGENT } from "@/constants/testIds";
import { ShieldAlert, MapPin, Banknote, ArrowRight, BadgeCheck, Star, AlertTriangle, Calendar } from "lucide-react";

export default function AgentDashboard() {
  const { user, refreshMe } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showKyc, setShowKyc] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tasks");
      setTasks(data);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const myTasks = sortByUrgency(tasks.filter((t) => t.agent_id === user?.user_id));
  const market = sortByUrgency(tasks.filter((t) => t.status === "open"));

  const verified = user?.verification_status === "verified";
  const profile = user?.agent_profile || {};

  return (
    <div className="min-h-screen">
      <NavBar />
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <section className="grid md:grid-cols-12 gap-6 mb-10">
          <div className="md:col-span-8 eb-card p-8">
            <span className="eb-chip">Agent control</span>
            <h1 className="mt-4 text-4xl">Hello, {user?.name?.split(" ")[0]}.</h1>
            <p className="mt-2 text-[var(--eb-text-dim)]">
              {verified
                ? "You're verified and live in the marketplace. Claim a task to start earning."
                : "Submit your KYC details to get verified. We typically review in under 24 hours."}
            </p>

            <div className="mt-6 flex flex-wrap gap-3">
              {!verified ? (
                <button data-testid={AGENT.kycBtn} onClick={() => setShowKyc(true)} className="eb-btn eb-btn-primary">
                  Submit KYC <ArrowRight className="w-4 h-4" />
                </button>
              ) : null}
            </div>
          </div>

          <div className="md:col-span-4 eb-card p-7">
            <div className="text-xs tracking-[0.2em] uppercase text-[var(--eb-text-dim)]">Verification</div>
            <div className="mt-3 flex items-center gap-3">
              {verified ? (
                <>
                  <BadgeCheck className="w-7 h-7 text-[var(--eb-success)]" />
                  <div>
                    <div className="font-[800] text-xl">Verified Agent</div>
                    <div className="text-xs text-[var(--eb-text-dim)]">Insured · Background-checked</div>
                  </div>
                </>
              ) : (
                <>
                  <ShieldAlert className="w-7 h-7 text-[var(--eb-secondary)]" />
                  <div>
                    <div className="font-[800] text-xl">{user?.verification_status || "Not verified"}</div>
                    <div className="text-xs text-[var(--eb-text-dim)]">Submit your KYC to go live</div>
                  </div>
                </>
              )}
            </div>
            {verified && (
              <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                <div className="eb-elev p-3">
                  <div className="text-xs text-[var(--eb-text-dim)]">Completed</div>
                  <div className="text-xl font-[800]">{profile.completed_tasks || 0}</div>
                </div>
                <div className="eb-elev p-3">
                  <div className="text-xs text-[var(--eb-text-dim)]">Rating</div>
                  <div className="text-xl font-[800] flex items-center gap-1"><Star className="w-4 h-4 text-[var(--eb-primary)] fill-[var(--eb-primary)]" /> {profile.rating || "—"}</div>
                </div>
              </div>
            )}
          </div>
        </section>

        {verified && (
          <>
            <section className="mb-10">
              <div className="flex items-end justify-between mb-5">
                <h2 className="text-2xl">My active tasks</h2>
                <span className="text-xs text-[var(--eb-text-dim)]">{myTasks.length} total</span>
              </div>
              {loading ? <Skeletons /> : myTasks.length === 0 ? (
                <div className="eb-card p-8 text-[var(--eb-text-dim)]">No assigned tasks yet — claim one from the marketplace below.</div>
              ) : (
                <div className="grid md:grid-cols-2 gap-5">
                  {myTasks.map((t) => <TaskRow key={t.task_id} t={t} />)}
                </div>
              )}
            </section>

            <section>
              <div className="flex items-end justify-between mb-5">
                <h2 className="text-2xl">Marketplace</h2>
                <span className="text-xs text-[var(--eb-text-dim)]">{market.length} open</span>
              </div>
              {market.length === 0 ? (
                <div className="eb-card p-8 text-[var(--eb-text-dim)]">No open tasks at the moment. Check back soon.</div>
              ) : (
                <div className="grid md:grid-cols-2 gap-5">
                  {market.map((t) => <MarketRow key={t.task_id} t={t} onClaimed={load} />)}
                </div>
              )}
            </section>
          </>
        )}

        {err && <div className="mt-6 text-[var(--eb-secondary)] text-sm">{err}</div>}
      </main>

      {showKyc && <KycModal onClose={() => setShowKyc(false)} onDone={async () => { await refreshMe(); setShowKyc(false); }} />}
    </div>
  );
}

function TaskRow({ t }) {
  const u = taskUrgency(t);
  return (
    <Link
      to={`/tasks/${t.task_id}`}
      data-urgency={u.bucket}
      className={`eb-card p-6 block hover:border-[var(--eb-primary)] transition-colors relative ${u.ringClass} ${u.bgClass}`}
    >
      <span className={`absolute top-3 right-3 w-2 h-2 rounded-full ${u.dotClass}`} aria-hidden="true" />
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--eb-text-dim)]">{t.category_label}</div>
          <div className="mt-1 text-xl font-[700]">{t.title}</div>
        </div>
        <StatusPill status={t.status} />
      </div>
      <p className="mt-3 text-sm text-[var(--eb-text-dim)] line-clamp-2">{t.description}</p>
      <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-[var(--eb-text-dim)]">
        <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {t.city}</span>
        {t.budget_ngn ? <span className="inline-flex items-center gap-1"><Banknote className="w-3.5 h-3.5" /> ₦{t.budget_ngn.toLocaleString()}</span> : null}
        {t.deadline ? <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {new Date(t.deadline).toLocaleDateString()}</span> : null}
        {u.label && (
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.15em] border ${u.badgeClass}`}>
            {u.bucket === "late" && <AlertTriangle className="w-3 h-3" />}
            {u.label}
          </span>
        )}
        <span className="ml-auto inline-flex items-center gap-1 text-[var(--eb-primary)]">Open <ArrowRight className="w-3.5 h-3.5" /></span>
      </div>
    </Link>
  );
}

function MarketRow({ t, onClaimed }) {
  const [busy, setBusy] = useState(false);
  const u = taskUrgency(t);
  const claim = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/tasks/${t.task_id}/claim`);
      onClaimed && onClaimed();
    } catch (e) {
      alert(formatApiError(e));
    } finally { setBusy(false); }
  };
  const canClaim = t.service_fee_status === "held_escrow";
  return (
    <Link
      to={`/tasks/${t.task_id}`}
      data-testid={AGENT.marketplaceCard}
      data-urgency={u.bucket}
      className={`eb-card p-6 block hover:border-[var(--eb-primary)] transition-colors relative ${u.ringClass} ${u.bgClass}`}
    >
      <span className={`absolute top-3 right-3 w-2 h-2 rounded-full ${u.dotClass}`} aria-hidden="true" />
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--eb-text-dim)]">{t.category_label}</div>
          <div className="mt-1 text-xl font-[700]">{t.title}</div>
        </div>
        <span className="eb-chip">{t.city}</span>
      </div>
      <p className="mt-3 text-sm text-[var(--eb-text-dim)] line-clamp-2">{t.description}</p>
      <div className="mt-3 flex flex-wrap gap-2 text-xs text-[var(--eb-text-dim)]">
        {t.deadline && (
          <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {new Date(t.deadline).toLocaleDateString()}</span>
        )}
        {u.label && (
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.15em] border ${u.badgeClass}`}>
            {u.bucket === "late" && <AlertTriangle className="w-3 h-3" />}
            {u.label}
          </span>
        )}
      </div>
      <div className="mt-4 flex items-center justify-between">
        <div className="text-xs text-[var(--eb-text-dim)]">
          Service fee: <span className="text-[var(--eb-primary)]">₦{t.service_fee_ngn?.toLocaleString()}</span>
        </div>
        {canClaim ? (
          <button onClick={claim} disabled={busy} className="eb-btn eb-btn-primary !py-2 !px-3 !text-xs">
            {busy ? "Claiming…" : "Claim task"}
          </button>
        ) : (
          <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--eb-text-faint)]">Awaiting client escrow</span>
        )}
      </div>
    </Link>
  );
}

function Skeletons() {
  return (
    <div className="grid md:grid-cols-2 gap-5">
      {[1, 2].map((i) => (
        <div key={i} className="eb-card p-6 animate-pulse h-32" />
      ))}
    </div>
  );
}

function KycModal({ onClose, onDone }) {
  const [form, setForm] = useState({ id_url: "", selfie_url: "", bio: "", city_coverage: "" });
  const [uploadingId, setUploadingId] = useState(false);
  const [uploadingSelfie, setUploadingSelfie] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const uploadField = async (field, file, setLoading) => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd);
      setForm((f) => ({ ...f, [field]: data.url }));
    } catch (e) { setErr(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/agents/kyc", {
        id_url: form.id_url || null,
        selfie_url: form.selfie_url || null,
        bio: form.bio,
        city_coverage: form.city_coverage.split(",").map((c) => c.trim()).filter(Boolean),
      });
      onDone();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="eb-card p-8 w-full max-w-lg eb-fadein">
        <h3 className="text-2xl">Agent KYC</h3>
        <p className="text-[var(--eb-text-dim)] mt-1">Help us verify you. We&apos;ll review in under 24 hours.</p>
        <div className="mt-5 space-y-4">
          <FileField
            label="Government-issued ID"
            testId={AGENT.kycIdFile}
            url={form.id_url}
            loading={uploadingId}
            onFile={(f) => uploadField("id_url", f, setUploadingId)}
          />
          <FileField
            label="Selfie holding the ID"
            testId={AGENT.kycSelfieFile}
            url={form.selfie_url}
            loading={uploadingSelfie}
            onFile={(f) => uploadField("selfie_url", f, setUploadingSelfie)}
          />
          <div>
            <label className="eb-label">City coverage (comma separated)</label>
            <input value={form.city_coverage} onChange={(e) => setForm({ ...form, city_coverage: e.target.value })} className="eb-input" placeholder="Lagos, Ikeja, Lekki" />
          </div>
          <div>
            <label className="eb-label">Short bio</label>
            <textarea value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="eb-input" rows="3" placeholder="5 years experience running errands…" />
          </div>
        </div>
        {err && <div className="mt-3 text-sm text-[var(--eb-secondary)]">{err}</div>}
        <div className="mt-6 flex gap-3 justify-end">
          <button onClick={onClose} className="eb-btn eb-btn-ghost">Cancel</button>
          <button data-testid={AGENT.kycSubmit} onClick={submit} disabled={busy} className="eb-btn eb-btn-primary">
            {busy ? "Submitting…" : "Submit KYC"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FileField({ label, testId, url, loading, onFile }) {
  return (
    <div>
      <label className="eb-label">{label}</label>
      <label className="eb-elev p-3 flex items-center gap-3 cursor-pointer hover:border-[var(--eb-primary)]">
        {url ? (
          <img src={url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url} alt="" className="w-14 h-14 object-cover rounded" />
        ) : (
          <div className="w-14 h-14 rounded bg-[var(--eb-surface)] border border-dashed border-[var(--eb-border-strong)] flex items-center justify-center text-xs text-[var(--eb-text-dim)]">JPG/PNG</div>
        )}
        <div className="text-sm flex-1">
          {loading ? "Uploading…" : url ? "Replace" : "Click to upload"}
          <div className="text-xs text-[var(--eb-text-faint)]">Max 5 MB</div>
        </div>
        <input
          data-testid={testId}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={(e) => onFile(e.target.files?.[0])}
          className="hidden"
        />
      </label>
    </div>
  );
}
